import React, { Component } from "react";
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButton } from "@ionic/react";

// css
import "../Shared.css";
import "../../App.css";
import "./DashboardRowCell.css";

// controllers
import {
  IScreenProps,
  IUser,
  IGuideline,
  IGuidelineSummary,
  IAlgorithm,
  ISearchResult,
  IClinicalNotice,
  IPathway,
  IPathwayBypassNumber,
  IPathwayWhatGoesWhere,
  IEssentialSection,
} from "../Interfaces";
import DataController from "../controllers/DataController";

// images
import guidelineIcon from "../assets/Icon_Search_Guidelines@3x.png";
import trustGuidelineIcon from "../assets/Icon_Search_Trust@3x.png";
import medIcon from "../assets/Icon_Search_Meds@3x.png";
import algorithmIcon from "../assets/Icon_Search_Algorithms@3x.png";
import pageForAgeIcon from "../assets/Icon_Search_PageforAge@3x.png";
import clinicalNoticeIcon from "../assets/Icon_Search_CNs@3x.png";
import pathwayIcon from "../assets/Icon_Search_Pathways@3x.png";
import essentialsIcon from "../assets/Icon_Search_Essentials@3x.png";

import guidelineIconDark from "../assets/Icon_Search_Guidelines_Dark@3x.png";
import trustGuidelineIconDark from "../assets/Icon_Search_Trust_Dark@3x.png";
import medIconDark from "../assets/Icon_Search_Meds_Dark@3x.png";
import algorithmIconDark from "../assets/Icon_Search_Algorithms_Dark@3x.png";
import pageForAgeIconDark from "../assets/Icon_Search_PageforAge_Dark@3x.png";
import clinicalNoticeIconDark from "../assets/Icon_Search_CNs_Dark@3x.png";
import pathwayIconDark from "../assets/Icon_Search_Pathways_Dark@3x.png";
import essentialsIconDark from "../assets/Icon_Search_Essentials_Dark@3x.png";

import indicatorBookmark from "../assets/Button_Bookmark_3Small@3x.png";
import disclosureRight from "../assets/Button_Disclosure_Right_Grey50@3x.png";

type Props = {
  guidelineSummary?: IGuidelineSummary;
  algorithm?: IAlgorithm;
  clinicalNotice?: IClinicalNotice;
  pathway?: IPathway | IPathwayBypassNumber | IPathwayWhatGoesWhere;
  essentialSection?: IEssentialSection;
  cellClicked?: () => void;
  darkMode: boolean;
  bookmarked: boolean;
};
type State = {};

class SearchResultCell extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
  }

  cellClicked() {
    if (this.props.cellClicked) {
      this.props.cellClicked();
    }
  }

  render() {
    let headerClass = "";
    let titleText = "";
    let typeText = "Guideline";
    let resultTrustLayout = null;
    let icon;

    let guidelineSummary = this.props.guidelineSummary;
    if (guidelineSummary != null) {
      if (guidelineSummary.Title) {
        titleText = guidelineSummary.Title;
      }

      if (guidelineSummary.SubTitle) {
        titleText += " " + guidelineSummary.SubTitle;
      }

      headerClass = DataController.getGuidelineClass(guidelineSummary);

      if (DataController.isPageForAge(guidelineSummary.GLID)) {
        titleText = "Page for Age: " + titleText.substr(14);
        icon = this.props.darkMode ? pageForAgeIconDark : pageForAgeIcon;
        typeText = "";
      } else if (DataController.isDrug(guidelineSummary)) {
        typeText = "Medicine";
        icon = this.props.darkMode ? medIconDark : medIcon;
      } else {
        let sectionName = DataController.getSectionTitle(guidelineSummary.Section);
        if (sectionName !== "") {
          typeText += " • " + sectionName;
        }

        if (guidelineSummary.trust) {
          icon = this.props.darkMode ? trustGuidelineIconDark : trustGuidelineIcon;
        } else {
          icon = this.props.darkMode ? guidelineIconDark : guidelineIcon;
        }
      }

      if (DataController.isPGD(guidelineSummary)) {
        typeText += " • PGD";
      } else if (DataController.isPMA(guidelineSummary)) {
        typeText += " • PMA";
      }

      if (guidelineSummary.trust) {
        headerClass = guidelineSummary.trust;
      }
    }

    let algorithm = this.props.algorithm;
    if (algorithm != null) {
      titleText = algorithm.name;
      headerClass = DataController.getSectionClass("Algorithms");
      typeText = "Algorithm";
      icon = this.props.darkMode ? algorithmIconDark : algorithmIcon;
    }

    let clinicalNotice = this.props.clinicalNotice;
    if (clinicalNotice != null) {
      titleText = clinicalNotice.Title;
      headerClass = DataController.getSectionClass("Clinical Notice");
      typeText = "Clinical Notice";
      icon = this.props.darkMode ? clinicalNoticeIconDark : clinicalNoticeIcon;
    }

    let pathway = this.props.pathway;
    if (pathway != null) {
      titleText = DataController.getPathwayName(pathway);
      headerClass = DataController.getSectionClass("Pathway");
      typeText = "Pathway";
      icon = this.props.darkMode ? pathwayIconDark : pathwayIcon;
    }

    let essentialSection = this.props.essentialSection;
    if (essentialSection != null) {
      if (essentialSection.Chapter.includes("Chapter")) {
        // remove number at beginning of section
        titleText = essentialSection.Section.substr(essentialSection.Section.indexOf(" ") + 1);
      } else {
        titleText = essentialSection.Section;
      }
      headerClass = DataController.getSectionClass("Essentials");
      typeText = "Essentials";
      icon = this.props.darkMode ? essentialsIconDark : essentialsIcon;
    }

    if (this.props.guidelineSummary?.trust) {
      typeText += " • ";

      resultTrustLayout = (
        <>
          <div className={`DashboardRowCellTrust ${DataController.getSectionClass(this.props.guidelineSummary.trust)}`}>
            {DataController.getTrustDisplayName(this.props.guidelineSummary.trust)}
          </div>
        </>
      );
    }

    return (
      <div className="DashboardRowCell" tabIndex={0} role="button" onClick={this.cellClicked.bind(this)}>
        <div className={`sliver ${headerClass}`} />
        <div className="DashboardRowCellContents">
          <div className="DashboardRowCellTitle" dangerouslySetInnerHTML={{ __html: titleText }} />

          <div className="DashboardRowCellLowerContent">
            <div className="DashboardRowCellInfo">
              <div className="DashboardRowCellType">
                <img className="DashboardRowCellIcon" src={icon} alt="" />
                {typeText}
              </div>
              {resultTrustLayout}
              {this.props.bookmarked ? (
                <img className="searchResultBookmarkIcon" src={indicatorBookmark} alt="" />
              ) : null}
            </div>
          </div>
        </div>
        <img alt="" src={disclosureRight} className="icon-44" />
      </div>
    );
  }
}

export default SearchResultCell;
